import React, { useState, useEffect } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  getColor,
  Icons,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import utilsCommon from "../../shared/utils/common";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import { t } from "i18next";
import CardDirectionIcon from "../../shared/icons/CardDirectionIcon";
import dayjs from "dayjs";
import utilsDocs from "../../shared/utils/docs";
import { handleDocPreview, handleDocDownload } from "../../shared/utils/api";
import { useNavigate } from "react-router-dom";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import LeaveCommentModal from "../../components/More/LeaveCommentModal";
import useLoginStore from "../../redux/dispatcher/useLoginStore";
import { ActionType, Member, Workgroup } from "../../types/workgroup";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import { nanoid } from "@reduxjs/toolkit";
import classNames from "classnames";
interface MyTasksProps {
  tasks: any[];
}

const MyTasks: React.FC<MyTasksProps> = ({ tasks }) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";
  const direction = document.documentElement.dir; // 'ltr' 또는 'rtl'

  /** Hook 정리 */
  const navigate = useNavigate();
  const toasts = useToast();
  const { getContentDownload } = useDocumentStore();
  const { readAlarm } = useLoginStore();
  const { workgroupState, removeTaskFromList, getMyDashboardTasks } =
    useWorkgroupStore();

  /** State 정리 */
  const [myTaskNum, changeMyTaskNum] = useState(0); // 현재 표시하고있는 task index
  const [openExtraModal, changeOpenExtraModal] = useState("CLOSE"); // 추가 모달 표시해야하는 경우
  const [extraCommentInfo, changeExtraCommentInfo] = useState<{
    syncId: string;
    contentName: string;
  }>(); // 의견 호출시 사용
  // My task 표시 목록  : title=타이틀, buttonType : 하위에 표시하는 버튼 목록
  const taskTypesContents = {
    invite: {
      title: t("header.setAlarm.inviteWorkgroup"),
      buttonType: ["workgroupMaster", "inviter"],
    },
    readRequest: {
      title: t("home.myTasks.readRequst"),
      buttonType: ["reqMasterUser", "requestUser"],
    },
    sign: {
      title: t("file.comment"),
      buttonType: ["comment"],
    },
    meeting: {
      title: t("home.myTasks.todayMeeting"),
      buttonType: ["meetingMaster", "startTime"],
    },
    share: {
      title: t("home.myTasks.receivedFile"),
      buttonType: ["managerName", "sender"],
    },
    succession: {
      title: t("home.myTasks.changeHead"),
      buttonType: ["oldWorkgroupMaster", "newWorkgroupMaster"],
    },
  } as Record<ActionType, { title: string; buttonType: string[] }>;
  const action: ActionType = tasks[myTaskNum].type as ActionType;
  const { title, buttonType } = taskTypesContents[action] ?? {
    title: "",
    buttonType: [],
  };

  /** 함수 정리 */
  /** 성공시, myTask에서 제거 */
  const handleReadAlarm = (req: { logId?: string; requestId?: string }) => {
    const { logId, requestId } = req;
    if (logId) {
      readAlarm({ logId });
      getMyDashboardTasks();
    } else if (requestId) {
      removeTaskFromList(requestId);
    }
  };
  const handleReadNorDownloadContent = (task: any) => {
    const { data } = task;

    if (
      utilsCommon.checkLicenseAvailable("allowWebView") &&
      utilsCommon.checkExtensionAvailable(
        task.data.contentName,
        "fwocExtensions"
      )
    ) {
      handleDocPreview({
        permission: "view",
        workgroupId: data.workgroupId,
        syncId: data?.syncId,
        toast: toasts,
        t,
        revisionNo: 0,
        noNeedToast: true,
      }).then((resp) => {
        if (resp !== "SUCCESS") {
          handleDocDownload(
            data.syncId,
            getContentDownload,
            toasts,
            t,
            "0",
            "",
            true
          ).then((resp1) => {
            if (resp1) {
              handleReadAlarm({
                logId: task.data?.logId,
                requestId: task.data?.requestId,
              });
              toasts.toastMsg(
                nanoid(),
                t("home.myTasks.alert.noViewSoDownload"),
                "success"
              );
            } else {
              toasts.toastMsg(
                nanoid(),
                t("home.myTasks.alert.noView"),
                "error"
              );
            }
          });
        } else {
          handleReadAlarm({
            logId: task.data?.logId,
            requestId: task.data?.requestId,
          });
        }
      });
    } else {
      handleDocDownload(data.syncId, getContentDownload, toasts, t).then(
        (resp) => {
          if (resp) {
            handleReadAlarm({
              logId: task.data?.logId,
              requestId: task.data?.requestId,
            });
          }
        }
      );
    }
  };
  /** 버튼 클릭시 이동 */
  const handleDoActionMyTask = async (task: any) => {
    const { type, data } = task;
    switch (type) {
      case "share":
      case "readRequest":
        handleReadNorDownloadContent(task);
        break;
      case "sign":
        handleDocPreview({
          permission: "view",
          workgroupId: data.workgroupId,
          syncId: data?.syncId,
          toast: toasts,
          t,
          revisionNo: 0,
          tab: "comment",
          noNeedToast: true,
        }).then((resp) => {
          if (resp?.includes("WEBVIEW")) {
            changeExtraCommentInfo({
              syncId: tasks[myTaskNum].data.syncId,
              contentName: tasks[myTaskNum].data.contentName,
            });
            changeOpenExtraModal("LEAVE_COMMENT");
          } else if (!resp?.includes("SUCCESS")) {
            toasts.toastMsg(nanoid(), t("home.myTasks.alert.noAuth"), "error");
          }
          handleReadAlarm({ logId: data.logId });
        });
        break;
      case "invite":
      case "succession":
        if (
          workgroupState.workgroupSidemenuList.filter(
            (item: Workgroup) => item.workgroupId === data.workgroupId
          )?.length > 0
        )
          navigate(
            `${process.env.PUBLIC_URL}/workgroup/${data?.workgroupId}/home`
          );
        else
          toasts.toastMsg(nanoid(), t("common.alert.noneWorkgroup"), "error");
        handleReadAlarm({ logId: data.logId });
        break;
      case "meeting":
        dayjs(task.data.startTime).valueOf() > dayjs().valueOf()
          ? navigate(
              `${process.env.PUBLIC_URL}/workgroup/${data?.workgroupId}/meeting`
            )
          : window.open(
              task.data.ownerId === myId
                ? task.data.startUrl
                : task.data.joinUrl
            );
        break;
    }
  };
  /** UI: MY Task - 하단 */
  const renderMyTaskFooterContent = (task: any) => {
    if (!task?.type || !task?.data) return;
    const { data } = task;
    const buttonContents = {
      workgroupMaster: {
        label: t("home.myTasks.head"),
        values: [data?.managerName, data?.managerId],
      },
      inviter: {
        label: t("home.myTasks.invitor"),
        values: [data.senderName, data.senderId],
      },
      newWorkgroupMaster: {
        label: t("home.myTasks.now"),
        values: [data?.managerName, data?.managerId],
      },
      oldWorkgroupMaster: {
        label: t("home.myTasks.before"),
        values: [data.senderName, data.senderId],
      },
      managerName: {
        label: t("file.master"),
        values: [data.managerName, data.managerId],
      },
      sender: {
        label: t("sendingFile.sender"),
        values: [data.senderName, data.senderId],
      },
      comment: {
        label: "",
        values: [data.msg],
      },
      reqMasterUser: {
        label: t("file.master"),
        values: [data.masterUserName, data.masterUser],
      },
      requestUser: {
        label: t("home.myTasks.requester"),
        values: [data.requestUserName, data.requestUser],
      },
      startTime: {
        label: t("meeting.startTime"),
        values: [dayjs(data.startTime).format("hh:mm A")],
      },
      meetingMaster: {
        label: t("meeting.host"),
        values: [
          data.participantList?.filter(
            (item: Member) => item.userId === data.ownerId
          )[0]?.userName,
          data.participantList?.filter(
            (item: Member) => item.userId === data.ownerId
          )[0]?.userId,
          data.ownerId,
        ],
      },
    } as Record<string, { label: string; values: string[] }>;

    return (
      <Box classes="home-myTask--footer">
        {buttonType?.map((item: string) => {
          const value = buttonContents[item].values.filter((item) => item)[0];
          return item === "comment" ? (
            <Box width={`calc((100% - 16px) * 2 / 3 + 8px)`}>
              <Avatar
                name={task?.data["senderName"]}
                uniqueCode={utilsCommon.getUserColor(task?.data["userType"])}
                classes="mg ml-12 mr-8"
                size="24"
              />
              <Tooltip message={task?.data["msg"]} width={"calc(100% - 56px)"}>
                <Typography ellipsis type="body3_rg" classes="comment">
                  {task?.data["msg"]}
                </Typography>
              </Tooltip>
            </Box>
          ) : (
            <Box direction="column" width={`calc((100% - 16px) / 3)`}>
              <Typography
                type="body4_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {buttonContents[item] ? buttonContents[item].label : ""}
              </Typography>
              <Tooltip maxWidth={"calc(100% - 15px)"} message={value}>
                <Typography type="body3_rg" classes="mg mt-2" ellipsis>
                  {value}
                </Typography>
              </Tooltip>
            </Box>
          );
        })}
        <Button
          label={
            ["readRequest", "share"].includes(task?.type)
              ? utilsCommon.checkLicenseAvailable("allowWebView") &&
                utilsCommon.checkExtensionAvailable(
                  task.data.contentName,
                  "fwocExtensions"
                )
                ? t("home.myTasks.preview")
                : t("common.download")
              : task.type === "meeting" &&
                dayjs(task.data.startTime).valueOf() > dayjs().valueOf()
              ? t("home.myTasks.detail")
              : t("home.myTasks.goNow")
          }
          btnStyle="fill"
          width={`calc((100% - 16px) / 3)`}
          style={{
            height: "48px",
            flexDirection: "column",
            padding: "0",
            alignItems: "center",
            justifyContent: "center",
          }}
          classes="home-myTask--btn"
          onClick={() => handleDoActionMyTask(task)}
        />
      </Box>
    );
  };
  /** UI: MY Task - 상단*/
  const renderMyTaskContent = (task: any) => {
    if (!task?.type || !task?.data) return;
    const { type, data } = task;
    switch (type) {
      case "readRequest":
      case "sign":
      case "share":
        return (
          <Box direction="column" alignItems="center" justifyContent="center">
            <Box classes="home-myTask--icon mg mt-4">
              <Icons
                variant="line"
                label={
                  type === "readRequest"
                    ? "alertOctagon"
                    : type === "share"
                    ? "document"
                    : "comment"
                }
                size={32}
                stroke={getColor("COLOR_AVATAR_GREEN_ICON")}
              />
            </Box>
            <Box
              classes="mg mt-8"
              width={"100%"}
              alignItems="center"
              justifyContent="center"
            >
              {utilsDocs.renderDocIcon(data.contentName, 24)}
              <Tooltip
                message={data.contentName}
                maxWidth={"calc(100% - 28px)"}
              >
                <Typography type="body2_rg" ellipsis classes="mg ml-4">
                  {data.contentName}
                </Typography>
              </Tooltip>
            </Box>
            <Tooltip
              message={
                data.workgroupId === "NONEWORKGROUP"
                  ? t("home.myTasks.myBoxLoc")
                  : data.workgroupName ?? data.workgroup.workgroupName
              }
            >
              <Typography
                type="body4_rg"
                classes={classNames("mg", type === "sign" ? "mt-4" : "mt-2")}
                ellipsis
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {data.workgroupId === "NONEWORKGROUP"
                  ? t("home.myTasks.myBoxLoc")
                  : data.workgroupName ?? data.workgroup.workgroupName}
              </Typography>
            </Tooltip>
          </Box>
        );
      case "meeting":
        return (
          <Box direction="column" alignItems="center">
            <Box classes="home-myTask--icon mg mt-8">
              <Icons
                variant="line"
                label={"video"}
                size={32}
                stroke={getColor("COLOR_AVATAR_GREEN_ICON")}
              />
              {dayjs(task.data.startTime).valueOf() < dayjs().valueOf() && (
                <Typography
                  classes="home-myTask--live"
                  type="caption_rg"
                  exactColor="white"
                >
                  LIVE
                </Typography>
              )}
            </Box>
            <Box classes="mg mt-8" maxWidth={"100%"}>
              <Tooltip message={data.topic}>
                <Typography type="body2_rg" ellipsis classes="mg ml-4">
                  {data.topic}
                </Typography>
              </Tooltip>
            </Box>
            <Tooltip message={data.workgroupName}>
              <Typography
                type="body4_rg"
                ellipsis
                classes="mg mt-4"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {data.workgroupName}
              </Typography>
            </Tooltip>
          </Box>
        );
      case "invite":
      case "succession":
        return (
          <Box direction="column" alignItems="center" classes="mg mt-8">
            <Avatar
              uniqueCode={data.workgroupId}
              shape="workgroup"
              name={data.workgroupName}
              classes="mg mt-12"
              size="48"
            />
            <Tooltip message={data.workgroupName}>
              <Typography type="body2_rg" ellipsis classes="mg mt-10">
                {data.workgroupName}
              </Typography>
            </Tooltip>
          </Box>
        );
      default:
        return type;
    }
  };
  return (
    <Box classes="home-myTask">
      {openExtraModal === "LEAVE_COMMENT" && (
        <LeaveCommentModal
          open={openExtraModal === "LEAVE_COMMENT"}
          simpleDocInfo={extraCommentInfo}
          onClose={() => {
            changeExtraCommentInfo(undefined);
            changeOpenExtraModal("CLOSE");
          }}
        />
      )}
      <Box alignItems="center ">
        <Typography type="subtitle1">My Tasks</Typography>
        <Badge
          count={tasks?.length}
          type="count"
          classes="mg ml-8"
          style={{ justifyContent: "center" }}
        />
      </Box>
      <Box classes="home-myTask--body" direction="column">
        <Box>
          <Typography
            type="subtitle2"
            exactColor={getColor("COLOR_TEXT_BRAND")}
          >
            {title}
          </Typography>
          {tasks?.length > 1 && (
            <Box
              classes="mg ml-auto"
              direction={direction === "ltr" ? "row" : "row-reverse"}
            >
              <CardDirectionIcon
                width={24}
                height={24}
                onClick={() =>
                  changeMyTaskNum(
                    myTaskNum - 1 >= 0 ? myTaskNum - 1 : tasks?.length - 1
                  )
                }
              />
              <Box width={8} />
              <CardDirectionIcon
                width={24}
                height={24}
                direction="right"
                onClick={() =>
                  changeMyTaskNum(
                    myTaskNum + 1 < tasks?.length ? myTaskNum + 1 : 0
                  )
                }
              />
            </Box>
          )}
        </Box>
        {renderMyTaskContent(tasks[myTaskNum])}
        {renderMyTaskFooterContent(tasks[myTaskNum])}
      </Box>
    </Box>
  );
};

export default MyTasks;
